import React, { useEffect, useState } from 'react'
import { Box, Fab, IconButton, Paper } from '@mui/material'
import DialpadIcon from '@mui/icons-material/Dialpad'
import CloseIcon from '@mui/icons-material/Close'
import Simulator from '../Simulator/Simulator'

// Renders the simulator as a floating, dismissible panel instead of a tab, so a business user can keep
// trying a flow while still looking at Sessions/Flows/Chat behind it - switching tabs used to hide the
// simulator entirely, which defeats comparing what it does against what a real session actually looked
// like. Rendered as a sibling of the Tabs (not inside a TabPanel), so it stays mounted and visible across
// tab changes; only the flow-viewer's own fullScreen dialog is allowed to cover it.
//
// autoOpen skips the dialpad FAB step - for callers (e.g. the home dashboard's "Get Policy via USSD" card)
// where clicking through to the simulator IS the whole point, an extra "now click here to actually open
// it" step would just be friction. Re-opens whenever msisdn/flowUuid changes so re-triggering with a new
// number works even after the panel was manually closed once.
export default function FloatingSimulator({ flowUuid, msisdn, autoOpen }) {
  const [open, setOpen] = useState(!!autoOpen)

  useEffect(() => {
    if (autoOpen) setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen, flowUuid, msisdn])

  if (!open) {
    return (
      <Fab
        color="primary"
        onClick={() => setOpen(true)}
        aria-label="Open simulator"
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
      >
        <DialpadIcon />
      </Fab>
    )
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1200,
        maxHeight: 'calc(100vh - 48px)',
        maxWidth: 'calc(100vw - 48px)',
        overflow: 'auto',
        borderRadius: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1
        }}
      >
        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          aria-label="Close simulator"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Simulator flowUuid={flowUuid} msisdn={msisdn} />
    </Paper>
  )
}
